import { useState } from 'react';
import { Table, Button, Modal, Input, Form, Popconfirm, message, Typography, Tag } from 'antd';
import { PlusOutlined, DeleteOutlined, StopOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getBlockedDomains,
  addBlockedDomain,
  removeBlockedDomain,
  type BlockedDomain,
} from '@/services/onboarding.service';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

export default function BlockedDomainsPage() {
  const [page, setPage] = useState(1);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [form] = Form.useForm<{ domain: string }>();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['blocked-domains', page],
    queryFn: () => getBlockedDomains(page, 20),
  });

  const domains: BlockedDomain[] = data?.data?.data ?? [];
  const total: number = data?.data?.total ?? 0;

  const addMutation = useMutation({
    mutationFn: (domain: string) => addBlockedDomain(domain),
    onSuccess: () => {
      message.success('Domain added to blocklist');
      setAddModalOpen(false);
      form.resetFields();
      queryClient.invalidateQueries({ queryKey: ['blocked-domains'] });
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? 'Failed to add domain');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => removeBlockedDomain(id),
    onSuccess: () => {
      message.success('Domain removed from blocklist');
      queryClient.invalidateQueries({ queryKey: ['blocked-domains'] });
    },
    onError: () => message.error('Failed to remove domain'),
  });

  const columns = [
    {
      title: 'Domain',
      dataIndex: 'domain',
      key: 'domain',
      render: (v: string) => (
        <span style={{ fontWeight: 600, color: '#0f172a', fontFamily: 'monospace' }}>{v}</span>
      ),
    },
    {
      title: 'Added By',
      dataIndex: 'addedBy',
      key: 'addedBy',
      render: (v: string) => (
        <Tag style={{ borderRadius: 12, fontFamily: 'monospace', fontSize: 11 }}>{v}</Tag>
      ),
    },
    {
      title: 'Added At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => (
        <span style={{ color: '#94a3b8', fontSize: 12 }}>{dayjs(v).format('DD MMM YYYY')}</span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      render: (_: unknown, record: BlockedDomain) => (
        <Popconfirm
          title="Remove this domain from blocklist?"
          description="Users will be able to register with this domain again."
          onConfirm={() => deleteMutation.mutate(record.id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            loading={deleteMutation.isPending}
          >
            Remove
          </Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <Title level={4} style={{ margin: 0, color: '#0f172a' }}>Blocked Email Domains</Title>
        <Text style={{ color: '#64748b', fontSize: 13 }}>
          Prevent registration from disposable or banned email domains
        </Text>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setAddModalOpen(true)}
          style={{ background: '#6366f1', borderColor: '#6366f1' }}
        >
          Add Domain
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={domains}
        rowKey="id"
        loading={isLoading}
        pagination={{
          current: page,
          total,
          pageSize: 20,
          onChange: setPage,
          showTotal: (t) => `${t} blocked domain${t !== 1 ? 's' : ''}`,
        }}
        scroll={{ x: 'max-content' }}
        style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}
        locale={{ emptyText: (
          <div style={{ padding: '40px 0', color: '#94a3b8' }}>
            <StopOutlined style={{ fontSize: 32, marginBottom: 8, display: 'block' }} />
            No blocked domains yet
          </div>
        ) }}
      />

      <Modal
        title="Add Blocked Domain"
        open={addModalOpen}
        onCancel={() => { setAddModalOpen(false); form.resetFields(); }}
        onOk={() => form.submit()}
        confirmLoading={addMutation.isPending}
        okText="Add to Blocklist"
        okButtonProps={{ style: { background: '#6366f1', borderColor: '#6366f1' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => addMutation.mutate(vals.domain)}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="domain"
            label="Domain"
            rules={[
              { required: true, message: 'Domain is required' },
              {
                pattern: /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z]{2,})+$/i,
                message: 'Enter a valid domain (e.g. mailinator.com)',
              },
            ]}
          >
            <Input placeholder="mailinator.com" style={{ fontFamily: 'monospace' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
